import { useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { api } from '../api';
import type {
  Landlord,
  LeaseDocument,
  LeaseInput,
  LeaseListRow,
  LeasePremiseUse,
  MarketingIndividual,
  Property,
  Tenant,
} from '../types';
import { Modal } from '../components/Modal';
import { LeaseForm } from '../components/LeaseForm';
import { PdfPreview } from '../components/PdfPreview';
import { RenewLeaseDialog } from '../components/RenewLeaseDialog';

const formatMoney = (v: string | null) => {
  if (!v) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const formatPct = (v: string | null) => {
  if (!v) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return `${(n * 100).toFixed(2)}%`;
};

const daysUntil = (date: string | null): number | null => {
  if (!date) return null;
  const target = new Date(`${date}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
};

const ExpiryBadge = ({ date }: { date: string | null }) => {
  const days = daysUntil(date);
  if (days === null || days > 90) return null;
  if (days < 0) return <span className="tag danger">Expired</span>;
  return <span className="tag warn">Expires in {days}d</span>;
};

const helper = createColumnHelper<LeaseListRow>();

const csvEscape = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename: string, header: string[], rows: string[][]) => {
  const lines = [header, ...rows].map((r) => r.map(csvEscape).join(','));
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const LeasesPage = () => {
  const [rows, setRows] = useState<LeaseListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [properties, setProperties] = useState<Property[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [landlords, setLandlords] = useState<Landlord[]>([]);
  const [marketing, setMarketing] = useState<MarketingIndividual[]>([]);
  const [premises, setPremises] = useState<LeasePremiseUse[]>([]);

  const [insertOpen, setInsertOpen] = useState(false);
  const [editLease, setEditLease] = useState<LeaseListRow | null>(null);
  const [renewFor, setRenewFor] = useState<LeaseListRow | null>(null);
  const [uploadFor, setUploadFor] = useState<LeaseListRow | null>(null);
  const [previewUrl, setPreviewUrl] = useState<{ url: string; title: string } | null>(null);

  const load = async (deleted = includeDeleted) => {
    setLoading(true);
    const r = await api.leases(deleted);
    setRows(r);
    setLoading(false);
  };

  useEffect(() => {
    void load();
    void api.properties().then(setProperties);
    void api.tenants().then(setTenants);
    void api.landlords().then(setLandlords);
    void api.marketingIndividuals().then(setMarketing);
    void api.leasePremiseUses().then(setPremises);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      helper.accessor('id', { header: 'ID', size: 60 }),
      helper.accessor('propertyName', { header: 'Property' }),
      helper.accessor('tenantName', { header: 'Tenant' }),
      helper.accessor('landlordName', { header: 'Landlord' }),
      helper.accessor('rentalCurrency', { header: 'CCY', size: 60 }),
      helper.accessor('monthlyRentalCalc', {
        header: 'Monthly rent',
        cell: (c) => formatMoney(c.getValue()),
      }),
      helper.accessor('expirationDate', {
        header: 'Expires',
        cell: (c) => {
          const v = c.getValue();
          return (
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              {v ?? ''}
              <ExpiryBadge date={v} />
            </span>
          );
        },
      }),
      helper.accessor('lastRenewalDate', { header: 'Last renewal' }),
      helper.accessor('status', {
        header: 'Status',
        cell: (c) => {
          const v = c.getValue();
          const cls =
            v === 'Active'
              ? 'tag ok'
              : v === 'Draft'
                ? 'tag muted'
                : v === 'Expired'
                  ? 'tag warn'
                  : 'tag danger';
          return <span className={cls}>{v}</span>;
        },
      }),
      helper.accessor('paidStatus', {
        header: 'Paid',
        cell: (c) => {
          const v = c.getValue();
          if (!v) return '';
          return <span className={`tag ${v === 'Paid' ? 'ok' : 'danger'}`}>{v}</span>;
        },
      }),
      helper.accessor('escalation', {
        header: 'Escalation',
        cell: (c) => formatPct(c.getValue()),
      }),
      helper.accessor('unit', { header: 'Unit' }),
      helper.accessor('sageCode', { header: 'Sage code', size: 90 }),
      helper.accessor('documentCount', { header: 'Docs', size: 60 }),
      helper.display({
        id: 'actions',
        header: 'Actions',
        enableColumnFilter: false,
        cell: ({ row }) => {
          const l = row.original;
          return (
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setEditLease(l)}>Edit</button>
              {!l.deletedAt && <button onClick={() => setRenewFor(l)}>Renew</button>}
              <button onClick={() => setUploadFor(l)}>Upload</button>
              {l.leaseAgreementUri && (
                <button
                  onClick={() =>
                    setPreviewUrl({
                      url: l.leaseAgreementUri!,
                      title: `${l.propertyName ?? ''} - ${l.tenantName ?? ''}`,
                    })
                  }
                >
                  PDF
                </button>
              )}
              {!l.deletedAt ? (
                <button
                  className="danger"
                  onClick={async () => {
                    if (!confirm(`Delete lease #${l.id}?`)) return;
                    await api.deleteLease(l.id);
                    await load();
                  }}
                >
                  Delete
                </button>
              ) : (
                <button
                  onClick={async () => {
                    await api.restoreLease(l.id);
                    await load();
                  }}
                >
                  Restore
                </button>
              )}
            </div>
          );
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter: search, columnFilters },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  });

  const exportCsv = () => {
    const yesNo = (v: boolean | null | undefined) =>
      v === true ? 'Yes' : v === false ? 'No' : '';
    const str = (v: string | number | null | undefined) =>
      v === null || v === undefined ? '' : String(v);

    const header = [
      'ID',
      'Name',
      'Property Name',
      'Tenant Name',
      'Landlord',
      'Rental currency',
      'Monthly rental calc',
      'Expiration date',
      'Status',
      'BO Date',
      'Beneficial occupation period',
      'Commencement date',
      'Deposit bank guarantee',
      'Escalation',
      'Execution date',
      'External rate',
      'External sqm',
      'Guarantor details',
      'Include boz inflation in escalation',
      'Internal rate',
      'Internal sqm',
      'Landlord allowable',
      'Lease addendum',
      'Lease addendums',
      'Lease duration months',
      'Lease premise use',
      'Lease registration fee',
      'Unit',
      'Management fee',
      'Marketing individual',
      'Mezzanine rate',
      'Mezzanine sqm',
      'Misc rate',
      'Misc sqm',
      'Umbrella rate',
      'Umbrella sqm',
      'Covered area rate',
      'Covered area sqm',
      'Store room rate',
      'Store room sqm',
      'Balcony rate',
      'Balcony sqm',
      'Notes',
      'Paid status',
      'Receipt',
      'Tenant',
      'Property',
      'Lease Agreement',
      'Other Files',
      'Sage code',
    ];

    const data = table.getRowModel().rows.map((r) => {
      const l = r.original;
      const name = [l.propertyName, l.tenantName].filter(Boolean).join(' - ');
      const otherFiles = Math.max(0, l.documentCount - (l.leaseAgreementUri ? 1 : 0));
      return [
        str(l.id),
        name,
        str(l.propertyName),
        str(l.tenantName),
        str(l.landlordName),
        str(l.rentalCurrency),
        str(l.monthlyRentalCalc),
        str(l.expirationDate),
        str(l.status),
        str(l.boDate),
        str(l.beneficialOccupationPeriod),
        str(l.commencementDate),
        str(l.depositBankGuarantee),
        str(l.escalation),
        str(l.executionDate),
        str(l.externalRate),
        str(l.externalSqm),
        str(l.guarantorDetails),
        yesNo(l.includeBozInflationInEscalation),
        str(l.internalRate),
        str(l.internalSqm),
        str(l.landlordAllowable),
        str(l.leaseAddendum),
        yesNo(l.leaseAddendums),
        str(l.leaseDurationMonths),
        str(l.leasePremiseUseName),
        str(l.leaseRegistrationFee),
        str(l.unit),
        str(l.managementFee),
        str(l.marketingIndividualName),
        str(l.mezzanineRate),
        str(l.mezzanineSqm),
        str(l.miscRate),
        str(l.miscSqm),
        str(l.umbrellaRate),
        str(l.umbrellaSqm),
        str(l.coveredAreaRate),
        str(l.coveredAreaSqm),
        str(l.storeRoomRate),
        str(l.storeRoomSqm),
        str(l.balconyRate),
        str(l.balconySqm),
        str(l.notes),
        str(l.paidStatus),
        str(l.receipt),
        str(l.tenantId),
        str(l.propertyId),
        l.leaseAgreementUri ?? '',
        otherFiles > 0 ? String(otherFiles) : '',
        str(l.sageCode),
      ];
    });

    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`leases-${stamp}.csv`, header, data);
  };

  const submitInsert = async (input: LeaseInput) => {
    await api.createLease(input);
    setInsertOpen(false);
    await load();
  };
  const submitEdit = async (input: LeaseInput) => {
    if (!editLease) return;
    await api.updateLease(editLease.id, input);
    setEditLease(null);
    await load();
  };

  return (
    <div className="page">
      <h1>Leases</h1>
      <div className="toolbar">
        <input
          className="search"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label style={{ display: 'flex', gap: 4, alignItems: 'center', margin: 0 }}>
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={async (e) => {
              setIncludeDeleted(e.target.checked);
              await load(e.target.checked);
            }}
          />
          show deleted
        </label>
        <span className="spacer" />
        <button onClick={exportCsv} disabled={loading || table.getRowModel().rows.length === 0}>
          Export CSV
        </button>
        <button className="primary" onClick={() => setInsertOpen(true)}>
          + Insert lease
        </button>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} onClick={h.column.getToggleSortingHandler()}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {h.column.getIsSorted() === 'asc'
                        ? ' ▲'
                        : h.column.getIsSorted() === 'desc'
                          ? ' ▼'
                          : ''}
                    </th>
                  ))}
                </tr>
              ))}
              {table.getHeaderGroups().map((hg) => (
                <tr key={`${hg.id}-filters`} className="filter-row">
                  {hg.headers.map((h) => (
                    <th key={h.id} onClick={(e) => e.stopPropagation()}>
                      {h.column.getCanFilter() ? (
                        <input
                          className="col-filter"
                          placeholder="filter"
                          value={(h.column.getFilterValue() as string) ?? ''}
                          onChange={(e) => h.column.setFilterValue(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((r) => (
                <tr key={r.id} style={r.original.deletedAt ? { opacity: 0.5 } : undefined}>
                  {r.getVisibleCells().map((c) => (
                    <td key={c.id}>{flexRender(c.column.columnDef.cell, c.getContext())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {insertOpen && (
        <Modal title="Insert lease" onClose={() => setInsertOpen(false)}>
          <LeaseForm
            properties={properties}
            tenants={tenants}
            landlords={landlords}
            marketingIndividuals={marketing}
            premiseUses={premises}
            onSubmit={submitInsert}
            onCancel={() => setInsertOpen(false)}
            submitLabel="Create"
          />
        </Modal>
      )}

      {editLease && (
        <Modal
          title={`Edit lease #${editLease.id}`}
          onClose={() => setEditLease(null)}
        >
          <LeaseForm
            initial={editLease}
            properties={properties}
            tenants={tenants}
            landlords={landlords}
            marketingIndividuals={marketing}
            premiseUses={premises}
            onSubmit={submitEdit}
            onCancel={() => setEditLease(null)}
            submitLabel="Save"
          />
        </Modal>
      )}

      {renewFor && (
        <RenewLeaseDialog
          leaseId={renewFor.id}
          leaseLabel={
            [renewFor.propertyName, renewFor.tenantName].filter(Boolean).join(' - ') ||
            `Lease #${renewFor.id}`
          }
          currentExpiration={renewFor.expirationDate}
          onClose={() => setRenewFor(null)}
          onRenewed={async () => {
            await load();
          }}
        />
      )}

      {uploadFor && (
        <UploadModal
          lease={uploadFor}
          onClose={async () => {
            setUploadFor(null);
            await load();
          }}
        />
      )}

      {previewUrl && (
        <PdfPreview
          url={previewUrl.url}
          title={previewUrl.title}
          onClose={() => setPreviewUrl(null)}
        />
      )}
    </div>
  );
};

const UploadModal = ({ lease, onClose }: { lease: LeaseListRow; onClose: () => void }) => {
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState('LeaseAgreement');
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState<LeaseDocument[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.documents(lease.id).then(setDocs);
  }, [lease.id]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.uploadDocument(lease.id, file, type);
      setFile(null);
      setDocs(await api.documents(lease.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'upload failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Documents — ${lease.propertyName ?? ''} - ${lease.tenantName ?? ''}`} onClose={onClose}>
      <div className="form-grid">
        <div className="field">
          <label>Document type</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option>LeaseAgreement</option>
            <option>Letter</option>
            <option>Addendum</option>
          </select>
        </div>
        <div className="field">
          <label>PDF</label>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <div className="full" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="primary" disabled={!file || busy} onClick={() => void upload()}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      <h2>Existing documents</h2>
      <table className="grid">
        <thead>
          <tr>
            <th>Type</th>
            <th>File</th>
            <th>Uploaded</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {docs.length === 0 && (
            <tr>
              <td colSpan={4} className="muted">
                None yet
              </td>
            </tr>
          )}
          {docs.map((d) => (
            <tr key={d.id}>
              <td>{d.documentType}</td>
              <td>
                <a
                  href={d.objectKey ? `/api/documents/${d.id}/file` : (d.legacyUri ?? '#')}
                  target="_blank"
                  rel="noreferrer"
                >
                  {d.originalFilename ?? d.legacyUri ?? `doc-${d.id}`}
                </a>
              </td>
              <td>{d.uploadedAt.slice(0, 10)}</td>
              <td>
                <button
                  className="danger"
                  onClick={async () => {
                    if (!confirm('Delete document?')) return;
                    await api.deleteDocument(d.id);
                    setDocs(await api.documents(lease.id));
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
};
